import IconSearchOutline from '@eduzz/houston-icons/SearchOutline';
import { Button, Dialog, DialogActions, Divider, Grid, Grow, IconButton, InputBase, InputLabel, Paper, Tooltip } from '@material-ui/core';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import InputAdornment from '@material-ui/core/InputAdornment';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import { useFormikContext } from 'formik';
import React, { useCallback, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useObservable } from 'react-use-observable';
import productService from 'v2/services/selectProduct';

import type { IChildren, ISelectProduct } from 'v2/interfaces/product/selectProduct';

import ListProductTree from './listProductTree';

import type { ISelectProductMultipleHandles } from '.';

const useStyles = makeStyles((theme) => ({
  dialog: {
    height: '80vh',
    width: '140vw',
  },
  root: {
    paddingTop: 0,
    overflow: 'hidden',
    display: 'flex',
    width: '100%',
  },
  search: {
    position: 'sticky',
    top: 0,
    zIndex: 99,
    marginBottom: 4,
  },
  message: {
    padding: 8,
    textAlign: 'center',
  },
  selectExpansion: {
    backgroundColor: '#f3f3f3',
  },
  inputProduct: {
    cursor: 'pointer',
    color: 'transparent',
    textShadow: '0 0 0 #1b1b1b',
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: '.3',
    },
  },
  rootInput: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    border: '1px solid #ced4da',
    marginTop: '0.5rem',
    boxShadow: '0 0px 0px 0 rgba(0,0,0,0.07)',
  },
  input: {
    marginLeft: theme.spacing(1),
    flex: 1,
  },
  iconButton: {
    fontSize: 20,
    '&:disabled': {
      cursor: 'not-allowed',
    },
  },
  button: {
    padding: 8,
  },
  divider: {
    height: 28,
    margin: 4,
  },
}));

interface IProps {
  endpoint?: string;
  disabled?: boolean;
  label?: React.ReactNode;
}

const MultipleSelectProductComponent: React.ForwardRefRenderFunction<ISelectProductMultipleHandles, IProps> = (
  { label, endpoint, disabled }: IProps,
  ref
) => {
  const classes = useStyles();
  const { setFieldValue } = useFormikContext<{ product_id: [] }>();
  const [modal, setModal] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>();
  const [selectedProducts, setSelectedProducts] = useState<Array<string>>([]);
  const [hasSelectedProduct, setHasSelectedProduct] = useState<boolean>(false);
  const [isLoadingProducts] = useObservable(() => productService.isLoadingProducts(), []);

  const [products] = useObservable(() => {
    return productService.loadProducts(endpoint);
  }, []);

  const getCheckedProducts = useCallback(() => {
    return (products || []).reduce((acc: Array<ISelectProduct | IChildren>, p: ISelectProduct) => {
      const children = (p.children || []).filter((c) => {
        // if (field?.value?.includes(c.id)) {
        //   c.checked = true;
        // }

        return c.checked;
      });

      if (p.checked) {
        //p.checked = true;
        acc = [...acc, p];
      }

      if (children.length) {
        children.forEach((c) => (acc = [...acc, c]));
      }

      return acc;
    }, []);
  }, [products]);

  useEffect(() => {
    const sp = getCheckedProducts().map((p: ISelectProduct) => `${p.id} - ${p.title}`);
    setSelectedProducts(sp);
    setHasSelectedProduct(sp && sp.length > 0);
  }, [getCheckedProducts]);

  const openModal = useCallback(() => {
    setModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setModal(false);
    setFilter('');
  }, []);

  const onChangeFilter = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFilter(value);
  }, []);

  const onSelect = useCallback(
    (id: number) => {
      const np = (products || []).map((prod: ISelectProduct) => {
        if (prod.id === Number(id)) {
          const status = !prod.checked;
          prod.checked = status;
          prod.children = (prod.children || []).map((p: IChildren) => {
            p.checked = status;
            return p;
          });
        } else {
          prod.children = (prod.children || []).map((p: IChildren) => {
            if (p.id === Number(id)) {
              const cStatus = !p.checked;
              p.checked = cStatus;
            }
            return p;
          });
        }
        return prod;
      });

      productService.setProducts(np);
      const ids = np.filter((prod) => prod.checked).map((p) => p.id);
      setFieldValue('product_id', ids);
    },
    [products, setFieldValue]
  );

  const unCheckAll = useCallback(() => {
    const np = (products || []).map((prod: ISelectProduct) => {
      prod.checked = false;

      prod.children = (prod.children || []).map((p: IChildren) => {
        p.checked = false;
        return p;
      });
      return prod;
    });

    productService.setProducts(np);
    setFieldValue('product_id', []);
  }, [products, setFieldValue]);

  const productsAndChildren =
    products?.length &&
    products.reduce((acc, p) => {
      let children = [] as any;

      acc = [...acc, p.title];

      if (p.children?.length) {
        p.children.map((prd: any) => (children = [...children, prd.title]));
      }

      acc = [...acc, ...children];
      return acc;
    }, []);

  const checkAll = useCallback(() => {
    const np = (products || []).map((prod: ISelectProduct) => {
      prod.checked = true;

      prod.children = (prod.children || []).map((p: IChildren) => {
        p.checked = true;
        return p;
      });
      return prod;
    });

    productService.setProducts(np);
    setFieldValue('product_id', []);
  }, [products, setFieldValue]);

  const handleClickSelectAll = useCallback(() => {
    const productsSelected = selectedProducts?.length;
    const allProducts = productsAndChildren?.length;

    if ((productsSelected > 0 && productsSelected < allProducts) || !productsSelected) {
      checkAll();
      return;
    }

    unCheckAll();
  }, [productsAndChildren, checkAll, selectedProducts, unCheckAll]);

  const onConfirm = useCallback(() => {
    const sp = getCheckedProducts().map((p: ISelectProduct) => p.id);

    setFieldValue('product_id', sp);
    closeModal();
  }, [closeModal, getCheckedProducts, setFieldValue]);

  useImperativeHandle(ref, () => {
    return {
      unCheckAll,
    };
  });

  const qtd = selectedProducts.length;

  const QuantitySelectedProduct = () => {
    let msg = 'Nenhum produto selecionado';
    if (qtd > 1) {
      msg = `${qtd} produtos selecionados`;
    } else if (qtd === 1) {
      msg = `${qtd} produto selecionado`;
    }
    return (
      <div style={{ textAlign: 'center', padding: 8, fontWeight: 600 }}>
        <span>{msg}</span>
      </div>
    );
  };

  const filteredProducts =
    products &&
    Array.isArray(products) &&
    products.filter((p: any) =>
      filter && filter.length > 0
        ? (p.id && p.id === Number(filter)) ||
          (p.title && p.title.toLowerCase().includes(filter.toLowerCase())) ||
          (p.id && String(p.id).includes(filter.toLowerCase())) ||
          (p.children && p.children.filter((c: any) => c.id === Number(filter) || c.title.toLowerCase().includes(filter.toLowerCase())).length > 0)
        : p
    );

  const tooltipTitle = selectedProducts.map((item, index) => {
    const limit = 25;

    if (index < limit) {
      return <p key={item}>{item}</p>;
    }

    if (index === limit) {
      return <p key={item}>...</p>;
    }

    return null;
  });

  return (
    <>
      {label && (
        <InputLabel htmlFor='multiple-products' shrink>
          {label}
        </InputLabel>
      )}

      <Paper className={classes.rootInput}>
        <Tooltip
          title={tooltipTitle}
          placement='bottom'
          disableFocusListener={!hasSelectedProduct}
          disableHoverListener={!hasSelectedProduct}
          disableTouchListener={!hasSelectedProduct}
        >
          <InputBase
            className={classes.input}
            placeholder={isLoadingProducts ? 'Carregando seus produtos...' : 'Selecione seus produtos'}
            value={hasSelectedProduct ? `${selectedProducts.length} Produto(s) ...` : ''}
            disabled={disabled}
            inputProps={{
              'aria-label': 'selecione seus produtos',
              className: classes.inputProduct,
              onClick: openModal,
            }}
          />
        </Tooltip>

        <IconButton className={classes.button} aria-label='search' onClick={openModal} disabled={disabled}>
          <SearchIcon className={classes.iconButton} />
        </IconButton>

        {hasSelectedProduct && (
          <>
            <Divider className={classes.divider} orientation='vertical' />
            <IconButton className={classes.button} color='primary' aria-label='close' onClick={unCheckAll}>
              <CloseIcon className={classes.iconButton} />
            </IconButton>
          </>
        )}
      </Paper>

      <Dialog
        open={modal}
        scroll='paper'
        aria-labelledby='scroll-dialog-title'
        aria-describedby='scroll-dialog-description'
        classes={{ paper: classes.dialog }}
        onClose={closeModal}
      >
        <DialogTitle id='alert-dialog-slide-title'>
          <Grid container justify='space-between' spacing={2}>
            <Grid item xs={12}>
              {isLoadingProducts ? 'Carregando seus produtos...' : 'Selecione seus produtos'}
            </Grid>
          </Grid>
        </DialogTitle>

        <DialogContent className={classes.root}>
          <DialogContentText id='scroll-dialog-description' style={{ width: '100%' }}>
            <TextField
              placeholder='Procure seu produto por título ou ID'
              fullWidth
              className={classes.search}
              onChange={onChangeFilter}
              value={filter}
              autoFocus
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconSearchOutline />
                  </InputAdornment>
                ),
              }}
            />

            <QuantitySelectedProduct />

            <ListProductTree
              productsAndChildren={productsAndChildren}
              selectedProducts={selectedProducts}
              handleClickSelectAll={handleClickSelectAll}
              itemSize={45}
              onSelect={onSelect}
              products={filteredProducts || []}
            />

            {!isLoadingProducts && (filteredProducts || []).length === 0 && (
              <Grow in={true}>
                <div className={classes.message}>
                  <p>Nenhum produto encontrado</p>
                </div>
              </Grow>
            )}

            {isLoadingProducts && (
              <Grow in={true}>
                <div className={classes.message}>
                  <p>Carregando produtos...</p>
                </div>
              </Grow>
            )}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button fullWidth onClick={closeModal}>
            Fechar
          </Button>

          <Button fullWidth variant='contained' color='secondary' onClick={onConfirm}>
            Confirmar ({qtd})
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const MultipleSelectProduct = forwardRef(MultipleSelectProductComponent);
